import * as systemConfig from 'constants/system'
import { AppState } from 'app/store'
import { initialState as defaultUserManagementState } from 'modules/login/reducer'

export { difference, union } from 'modules/arplaybook/helper'

export interface IMembership {
    isExpired: boolean
    isExpiringWithinWeek: boolean
    [key: string]: unknown
}

const storage = window.localStorage
const sessionStorage = window.sessionStorage
const tokenKey = systemConfig.TOKEN_IDENTIFIER
const membershipKey = systemConfig.MEMBERSHIP_IDENTIFIER

export function loadStateToken() {
    let token = sessionStorage.getItem(tokenKey)
    let membership = sessionStorage.getItem(membershipKey)
    if (!token) {
        token = storage.getItem(tokenKey)
    }
    if (!membership) {
        membership = storage.getItem(membershipKey)
    }
    if (token && membership) {
        return {
            userManagement: {
                ...defaultUserManagementState,
                userTypeAssertions: getUserTypeAssertions(token),
                isAuthenticated: true,
                token,
            },
        }
    }
    return { userManagement: { ...defaultUserManagementState, isAuthenticated: false } }
}

export function saveStateToken(state: AppState) {
    if (!state.userManagement.token) {
        storage.removeItem(tokenKey)
        sessionStorage.removeItem(tokenKey)
        return
    }
    return storage.setItem(tokenKey, state.userManagement.token)
}

export const openNewTabForUser = (t: string, m?: IMembership) => {
    const w = window.open('')
    if (w && t.length > 0) {
        w.sessionStorage.setItem(tokenKey, t)
        if (m) {
            w.sessionStorage.setItem(membershipKey, JSON.stringify(m))
        }
        w.location = window.location.origin
        return
    }
    throw Error('Could not open new window')
}

export function copyToClipboard(text: string) {
    const tempElement = document.createElement('input')
    document.body.appendChild(tempElement)
    tempElement.value = text
    tempElement.select()
    document.execCommand('copy')
    tempElement.parentNode?.removeChild(tempElement)
}

export function getTextFromClipboard() {
    const tempElement = document.createElement('textarea')
    document.body.appendChild(tempElement)
    tempElement.focus()
    document.execCommand('paste')
    const data = tempElement.textContent
    tempElement.parentNode?.removeChild(tempElement)
    return data
}

export const camelToSnakeCase = (str: string): string =>
    str.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)

export const parseJwt = (token: string) => {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            })
            .join(''),
    )

    return JSON.parse(jsonPayload)
}

export const getUserTypeAssertions = (
    t: string,
): {
    isTeamMember: boolean
    isTeamOwner: boolean
    isSuperAdmin: boolean
    isCustomerSupportUser: boolean
} => {
    const userTypeAssertions = {
        isTeamMember: false,
        isTeamOwner: false,
        isSuperAdmin: false,
        isCustomerSupportUser: false,
    }
    if (!t) {
        return userTypeAssertions
    }
    const { data: userData } = parseJwt(t)
    switch (userData?.role) {
        case 'TEAM_MEMBER':
            userTypeAssertions.isTeamMember = true
            break
        case 'TEAM_OWNER':
            userTypeAssertions.isTeamOwner = true
            break
        case 'SUPER_ADMIN':
            userTypeAssertions.isSuperAdmin = true
            break
        case 'CUSTOMER_SUPPORT':
            userTypeAssertions.isCustomerSupportUser = true
            break
        default:
            break
    }
    return userTypeAssertions
}

export const isTeamMember = (t: string): boolean => {
    if (!t) {
        return false
    }
    const { data: userData } = parseJwt(t)
    return userData?.role === 'TEAM_MEMBER'
}

export const isTeamOwner = (t: string): boolean => {
    if (!t) {
        return false
    }
    const { data: userData } = parseJwt(t)
    return userData?.role === 'TEAM_OWNER'
}

export const isSuperAdmin = (t: string): boolean => {
    if (!t) {
        return false
    }
    const { data: userData } = parseJwt(t)
    return userData?.role === 'SUPER_ADMIN'
}

export const isCustomerSupportUser = (t: string): boolean => {
    if (!t) {
        return false
    }
    const { data: userData } = parseJwt(t)
    return userData?.role === 'CUSTOMER_SUPPORT'
}

export enum SubscriptionStatus {
    UNKNOWN = 'UNKNOWN',
    IS_EXPIRED = 'Subscription expired',
    IS_EXPIRING_WITHIN_WEEK = 'Subscription expiring within a week',
    IS_VALID = 'Subscription valid for more than a week',
}

export const getSubscriptionStatus = (membership: IMembership): SubscriptionStatus => {
    if (membership.isExpired) {
        return SubscriptionStatus.IS_EXPIRED
    }
    if (membership.isExpiringWithinWeek) {
        return SubscriptionStatus.IS_EXPIRING_WITHIN_WEEK
    }
    return SubscriptionStatus.IS_VALID
}

export const hasSubscriptionExpired = (validTill: string): boolean => {
    const now = new Date().getTime()
    const validTillDate = new Date(validTill).getTime()

    return now > validTillDate
}

export const getDaysUntilExpiry = (validTill: string): number => {
    const now = new Date().getTime()
    const validTillDate = new Date(validTill).getTime()

    if (now > validTillDate) {
        return 0
    }

    const oneDayInMilliseconds = 24 * 60 * 60 * 1000
    const timeDifference = validTillDate - now
    const daysLeft = Math.ceil(timeDifference / oneDayInMilliseconds)

    return daysLeft
}

export const downloadDataUrl = (dataUrl: string, fileName: string = 'download') => {
    const a = document.createElement('a')
    a.href = dataUrl
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

export function stripProtocol(url: string) {
    return url.replace(/^https?:\/\//, '')
}

export function getKeyByValue(obj: Record<string, string>, value: string): string | undefined {
    return Object.keys(obj).find(key => obj[key] === value)
}

export const getRandomHexColor = (count = 1) => {
    const colors: string[] = []

    for (let i = 0; i < count; i++) {
        const randomColor = Math.floor(Math.random() * 0xffffff)
            .toString(16)
            .padStart(6, '0')
        colors.push(`#${randomColor}`)
    }

    return colors
}

export const get30DaysAgoDate = () => {
    const date = new Date()
    date.setDate(date.getDate() - 30)
    return date.toISOString().split('T')[0]
}

export const getTodayDate = () => {
    return new Date().toISOString().split('T')[0]
}

export const formatDateForDisplay = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

export const isUserLoggedinWithToken = (TOKEN_KEY: string): boolean => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) {
        return false
    }
    const isLoggedin = !isTokenExpired(token)
    if (!isLoggedin) {
        localStorage.removeItem(TOKEN_KEY)
    }
    return isLoggedin
}

export const isTokenExpired = (token: string): boolean => {
    if (!token) {
        return true
    }

    try {
        // Decode the payload (middle part of JWT)
        const payload = JSON.parse(atob(token.split('.')[1]))

        // Check if exp claim exists
        if (!payload.exp) {
            return false
        }

        // Compare exp (in seconds) with current time
        const currentTime = Math.floor(Date.now() / 1000)
        return payload.exp < currentTime
    } catch (error) {
        console.error('Error decoding token:', error)
        return true // Treat invalid tokens as expired
    }
}
